import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/hooks/auth/auth";
import { normalizeAuthRedirect } from "@/lib/auth-redirect";
import { toast } from "sonner";
import { useAuthSendTwoFactorVerificationCode, useAuthVerifyTwoFactorCode } from "@/orval/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { SendIcon } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { z } from "zod";

const twoFactorCodeSearchSchema = z.object({
  provider: z.string().catch("Email"),
  email: z.string().catch(""),
  redirect: z.string().optional(),
});

const TwoFactorCode = () => {
  const { provider, email, redirect } = Route.useSearch();
  const [value, setValue] = useState("");
  const [didAutoSubmit, setDidAutoSubmit] = useState(false);
  const [didResendOTP, setDidResendOTP] = useState(false);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, setToken } = useAuth();
  const inputOTPRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: submitOTPAsync, isPending } = useAuthVerifyTwoFactorCode();
  const { mutateAsync: resendOTPAsync, isPending: isResendingOTP } = useAuthSendTwoFactorVerificationCode();

  useLayoutEffect(() => {
    // Add a delay to allow the token to be set before redirecting
    if (isAuthenticated) {
      window.location.href = normalizeAuthRedirect(redirect, window.location.origin);
    }
  }, [isAuthenticated, navigate, redirect]);

  const onChange = (newValue: string) => {
    setValue(newValue);

    if (newValue.length === 6 && !didAutoSubmit) {
      // Submit the OTP
      setDidAutoSubmit(true);
      submitOTP(newValue);
    }
  };

  const submitOTP = async (code: string) => {
    if (code.length !== 6) {
      toast.error("Please enter a 6-digit code.");
      return;
    }

    setHasError(false);

    try {
      const result = await submitOTPAsync({ data: { code, provider, userNameOrEmail: email } });

      if (result && result.token) {
        setToken(result.token);
        toast.success("Two-factor authentication successful!");
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    }
  };

  const resendOTP = async () => {
    setDidResendOTP(true);
    setHasError(false);
    setValue("");
    setDidAutoSubmit(false); // Reset auto-submit flag when resending

    try {
      await resendOTPAsync({ data: { provider, userNameOrEmail: email } });

      toast.success(`The OTP has been sent to your ${provider.toLowerCase()}.`);

      inputOTPRef.current?.focus();
    } catch {
      toast.error("Failed to resend the OTP. Please try again");
      setDidResendOTP(false); // Reset on error so user can try again
    }
  };

  return (
    <div className="flex items-center justify-center flex-1">
      <Card className="mx-auto max-w-md grid gap-6">
        <CardHeader>
          <CardTitle>Enter OTP</CardTitle>
          <CardDescription>Enter the 6-digit code that was sent to your {provider.toLowerCase()}.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col items-center">
            <InputOTP
              ref={inputOTPRef}
              value={value}
              onChange={onChange}
              pattern={REGEXP_ONLY_DIGITS}
              maxLength={6}
              autoFocus
              autoComplete="one-time-code"
              data-1p-ignore
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>

            {didAutoSubmit && hasError && (
              <div className="flex flex-col gap-2 mt-4">
                <div className="text-red-600 text-sm mt-2">The code you entered is invalid. Please try again.</div>

                {!didResendOTP && (
                  <Button
                    type="button"
                    tabIndex={-1}
                    variant="link"
                    onClick={() => resendOTP()}
                    disabled={isResendingOTP}
                    className="text-blue-600 text-sm"
                  >
                    Send OTP again
                  </Button>
                )}
              </div>
            )}

            <IconButton
              disabled={isPending}
              type="button"
              onClick={() => submitOTP(value)}
              icon={<SendIcon />}
              className="w-full mt-8"
            >
              Submit OTP
            </IconButton>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/account/two-factor-code")({
  validateSearch: twoFactorCodeSearchSchema,
  component: TwoFactorCode,
});
